import { useParams } from "react-router";
import {
  useChangeRequestStateMutation,
  useGetRequestDetailQuery,
} from "./queries";
import ContentWrapper from "../../components/layout/ContentWrapper";
import { Button, PageHeading } from "../../components";
import RequestInfoBox from "./components/RequestInfoBox";
import RequestChat from "./components/RequestChat";
import RequestStateTag from "./components/RequestStateTag";
import { handleApiErrorWithNotification } from "../../helpers/errorHandling";
import { NetworkError } from "../../common/models";
import { CPRequestStateChange } from "../../api/models/unified";
import { useCustomTranslation } from "../../helpers/translateHelpers";
import { useCallback, useState } from "react";
import RequestResolveConfirmModal from "./components/RequestResolveConfirmModal";
import RequestCancelConfirmModal from "./components/RequestCancelConfirmModal";

type RequestDetailParams = "requestId";

const RequestDetailPage = () => {
  const { tRequest } = useCustomTranslation();
  const params = useParams<RequestDetailParams>();

  const query = useGetRequestDetailQuery(params.requestId);
  const changeRequestStateMutation = useChangeRequestStateMutation();

  const [showResolveConfirm, setShowResolveConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const requestDetail = query.data;

  const handleChangeState = useCallback(
    async (state: CPRequestStateChange) => {
      try {
        await changeRequestStateMutation.mutateAsync({
          requestId: params.requestId ?? "",
          values: { changeStateTo: state },
        });
        setShowResolveConfirm(false);
        setShowCancelConfirm(false);
      } catch (e) {
        handleApiErrorWithNotification(e as NetworkError);
      }
    },
    [changeRequestStateMutation, params.requestId],
  );

  return (
    <ContentWrapper
      loading={query.isLoading || !requestDetail}
      htmlTitle={requestDetail?.title}
    >
      {requestDetail && (
        <>
          <div className="mn-8 flex items-center justify-between">
            <PageHeading
              title={requestDetail.title}
              backButtonPath="requests"
            />
            <div className="flex flex-row items-center gap-4">
              {requestDetail.state !== "Cancelled" &&
                requestDetail.state !== "Resolved" && (
                  <>
                    <Button
                      label={tRequest("cancel")}
                      className="bg-error hover:bg-error text-white hover:bg-gradient-to-t hover:from-[rgba(0,0,0,0.20)] hover:to-[rgba(0,0,0,0.20)]"
                      onClick={() => setShowCancelConfirm(true)}
                      size="small"
                    />
                    <Button
                      label={tRequest("resolve")}
                      type="button"
                      onClick={() => setShowResolveConfirm(true)}
                      className="bg-success text-white hover:bg-gradient-to-t hover:from-[rgba(0,0,0,0.20)] hover:to-[rgba(0,0,0,0.20)]"
                      size="small"
                    />
                  </>
                )}
              <RequestStateTag state={requestDetail.state} />
            </div>
          </div>
          <RequestInfoBox detail={requestDetail} />
          <hr className="border-gray bg-gray my-4" />
          <RequestChat detail={requestDetail} />
        </>
      )}
      <RequestResolveConfirmModal
        open={showResolveConfirm}
        onClose={() => setShowResolveConfirm(false)}
        onConfirm={() => handleChangeState("Resolved")}
      />
      <RequestCancelConfirmModal
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => handleChangeState("Cancelled")}
      />
    </ContentWrapper>
  );
};

export default RequestDetailPage;
